/**
 * File Validation Error Service
 *
 * Maps backend file validation errors to user-friendly UI messages.
 * Use this service in upload components to display inline error messages.
 */

import { Injectable } from '@angular/core';

export interface FileValidationError {
    errorCode: string;
    message: string;
    details: {
        filename?: string;
        declaredType?: string;
        detectedType?: string;
        fileSizeMB?: number;
        maxSizeMB?: number;
        supportedFormats?: string[];
    };
}

export interface ErrorDisplayConfig {
    title: string;
    body: string;
    helperText?: string;
    severity: 'error' | 'warning';
}

@Injectable({
    providedIn: 'root'
})
export class FileValidationErrorService {
    /**
     * Check if an error response is a file validation error
     */
    isFileValidationError(error: any): boolean {
        return error?.errorCode && this.getSupportedErrorCodes().includes(error.errorCode);
    }

    /**
     * Get supported error codes
     */
    getSupportedErrorCodes(): string[] {
        return ['FILE_TYPE_MISMATCH', 'UNSUPPORTED_FILE_TYPE', 'FILE_TOO_LARGE', 'FILE_UNREADABLE'];
    }

    /**
     * Map backend error to user-facing display configuration
     */
    mapErrorToDisplay(error: FileValidationError): ErrorDisplayConfig {
        const { errorCode, details } = error;

        switch (errorCode) {
            case 'FILE_TYPE_MISMATCH':
                return {
                    title: 'File type mismatch',
                    body: `This file is named as a ${details.declaredType}, but it is actually a ${details.detectedType}. Renaming a file does not convert it. Please convert the file to ${details.declaredType} or upload it as ${details.detectedType}.`,
                    helperText: 'Supported formats: JPEG, PNG, GIF.',
                    severity: 'error'
                };

            case 'UNSUPPORTED_FILE_TYPE':
                return {
                    title: 'Unsupported file type',
                    body: "This file type isn't supported for attachments. Please upload a JPEG, PNG, GIF, PDF or MP4.",
                    helperText: details.detectedType ? `Detected type: ${details.detectedType}` : undefined,
                    severity: 'error'
                };

            case 'FILE_TOO_LARGE':
                const currentSize = details.fileSizeMB?.toFixed(2) || 'unknown';
                const maxSize = details.maxSizeMB?.toFixed(2) || '5.00';
                return {
                    title: 'File too large',
                    body: `This file is ${currentSize} MB. The maximum supported size is ${maxSize} MB. Please resize or compress the file and try again.`,
                    helperText: 'Tip: Use an online file compressor to reduce file size.',
                    severity: 'error'
                };

            case 'FILE_UNREADABLE':
                return {
                    title: "We couldn't read this file",
                    body: 'This file may be corrupted or incomplete. Please re-export the file (JPEG/PNG/GIF/PDF) and upload again.',
                    helperText: 'Try saving the file again from your file editor.',
                    severity: 'error'
                };

            default:
                return {
                    title: 'Upload failed',
                    body: 'There was a problem uploading this file. Please try again.',
                    helperText: 'Supported File Types and Limits: 5 MB for GIF, JPEG, PNG and 0.5 MB for MP4 and PDF.',
                    severity: 'error'
                };
        }
    }
}
